// GroVision 관리자 — MQTT 모니터링 (ADMIN 전용)
//
// 권장안: 기존 인프라 재사용 — 실시간 트래픽은 /ws/realtime WebSocket 구독,
// 장비/세션 정보는 GET /api/devices. 백엔드 변경 없음.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DeviceInfo } from '@grosync/common';
import {
  AdminTopBar, ViewScroll, Panel, StatCard, StatusPill, Sym, type DeviceStatus,
} from '../../ui';
import { fetchDevices } from '../../api/devices';
import { useAuthStore } from '../../store/authStore';

// ---------------------------------------------------------------------------
// WebSocket 실시간 피드
// ---------------------------------------------------------------------------

const MAX_FEED = 300;

type ConnState = 'connecting' | 'open' | 'closed';

interface FeedMsg {
  id: string;
  deviceId: string;
  sensorType: string;
  value: number;
  unit: string;
  anomaly: string;
  timestamp: string;
  rxAt: number;
}

interface WsEnvelope {
  type: string;
  clientCount?: number;
  data?: {
    id: string;
    deviceId: string;
    sensorType: string;
    value: number;
    unit: string;
    anomaly: string;
    quality: number | null;
    timestamp: string;
  };
}

function realtimeUrl(token: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/realtime?token=${encodeURIComponent(token)}`;
}

interface BrokerFeed {
  messages: FeedMsg[];
  conn: ConnState;
  clientCount: number | null;
  paused: boolean;
  setPaused: (p: boolean) => void;
  clear: () => void;
}

function useBrokerFeed(): BrokerFeed {
  const token = useAuthStore((s) => s.token);
  const [messages, setMessages] = useState<FeedMsg[]>([]);
  const [conn, setConn] = useState<ConnState>('connecting');
  const [clientCount, setClientCount] = useState<number | null>(null);
  const [paused, setPausedState] = useState(false);
  const pausedRef = useRef(false);

  const setPaused = (p: boolean): void => {
    pausedRef.current = p;
    setPausedState(p);
  };
  const clear = (): void => setMessages([]);

  useEffect(() => {
    if (!token) return;
    let stopped = false;
    let ws: WebSocket | null = null;
    let reconnectTimer: number | null = null;

    const connect = (): void => {
      if (stopped) return;
      setConn('connecting');
      ws = new WebSocket(realtimeUrl(token));

      ws.onopen = (): void => setConn('open');

      ws.onmessage = (ev: MessageEvent): void => {
        let msg: WsEnvelope;
        try {
          msg = JSON.parse(String(ev.data)) as WsEnvelope;
        } catch {
          return;
        }
        if (msg.type === 'connected' && typeof msg.clientCount === 'number') {
          setClientCount(msg.clientCount);
          return;
        }
        if (msg.type === 'sensor_reading' && msg.data && !pausedRef.current) {
          const d = msg.data;
          const item: FeedMsg = {
            id: d.id,
            deviceId: d.deviceId,
            sensorType: d.sensorType,
            value: d.value,
            unit: d.unit,
            anomaly: d.anomaly,
            timestamp: d.timestamp,
            rxAt: Date.now(),
          };
          setMessages((prev) => {
            const next = [item, ...prev];
            return next.length > MAX_FEED ? next.slice(0, MAX_FEED) : next;
          });
        }
      };

      ws.onclose = (): void => {
        setConn('closed');
        if (!stopped) reconnectTimer = window.setTimeout(connect, 3000);
      };

      ws.onerror = (): void => ws?.close();
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [token]);

  return { messages, conn, clientCount, paused, setPaused, clear };
}

// ---------------------------------------------------------------------------
// 헬퍼
// ---------------------------------------------------------------------------

function toUiStatus(s: DeviceInfo['status']): DeviceStatus {
  if (s === 'ACTIVE') return 'ok';
  if (s === 'WARNING' || s === 'ERROR') return 'warn';
  return 'off';
}

const pad2 = (n: number): string => String(n).padStart(2, '0');
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}
function fmtLastSeen(iso: string | null): string {
  if (!iso) return '기록 없음';
  const d = new Date(iso);
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function fmtValue(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

const ANOMALY_META: Record<string, { label: string; c: string }> = {
  normal: { label: '정상', c: 'var(--md-sys-color-on-surface-variant)' },
  warning: { label: '경고', c: 'var(--gs-warning)' },
  critical: { label: '위험', c: 'var(--md-sys-color-error)' },
};

const CONN_META: Record<ConnState, { label: string; c: string }> = {
  connecting: { label: '연결 중', c: 'var(--gs-warning)' },
  open: { label: '연결됨', c: 'var(--gs-primary-50)' },
  closed: { label: '끊김', c: 'var(--md-sys-color-error)' },
};

// ---------------------------------------------------------------------------
// 메인 뷰
// ---------------------------------------------------------------------------

const FEED_COLS = '92px 1.3fr 2fr 0.9fr 0.7fr';
const SESSION_COLS = '1.4fr 0.7fr 1fr 1.6fr 0.8fr';

export default function MqttView(): React.ReactElement {
  const { messages, conn, clientCount, paused, setPaused, clear } = useBrokerFeed();
  const devicesQ = useQuery<DeviceInfo[]>({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    refetchInterval: 30_000,
  });
  const devices = useMemo(() => devicesQ.data ?? [], [devicesQ.data]);

  const nameById = useMemo(
    () => new Map(devices.map((d) => [d.id, d.name])),
    [devices],
  );

  const online = devices.filter((d) => d.status !== 'OFFLINE').length;
  const offline = devices.length - online;

  // 분당 수신 — 최근 60초 피드 건수 (1초마다 갱신)
  const [, force] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => force((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);
  const perMin = messages.filter((m) => Date.now() - m.rxAt < 60_000).length;

  const connMeta = CONN_META[conn];

  return (
    <>
      <AdminTopBar title="MQTT 모니터링" subtitle="브로커 실시간 토픽 트래픽 · 장비 연결 상태" />

      <ViewScroll gap={18}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <StatCard icon="sensors" label="전체 장비" value={devices.length} unit="대" tone="secondary" />
          <StatCard icon="wifi" label="온라인" value={online} unit="대" tone="primary" />
          <StatCard icon="wifi_off" label="오프라인" value={offline} unit="대" tone="muted" />
          <StatCard icon="bolt" label="분당 수신" value={perMin} unit="건" tone="secondary" sub="최근 60초" />
        </div>

        {/* 실시간 토픽 트래픽 */}
        <Panel
          style={{ flexShrink: 0 }}
          title="실시간 토픽 트래픽"
          subtitle={clientCount != null ? `구독 클라이언트 ${clientCount}` : '브로커 수신 메시지'}
          pad={false}
          action={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: '600 12px/1 var(--gs-font-kr)', color: connMeta.c }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: connMeta.c }} />
                {connMeta.label}
              </span>
              <FeedButton icon={paused ? 'play_arrow' : 'pause'} label={paused ? '재개' : '일시정지'} onClick={() => setPaused(!paused)} />
              <FeedButton icon="delete_sweep" label="지우기" onClick={clear} />
            </div>
          }
        >
          <div className="gs-scroll-hide" style={{ height: 'clamp(360px, calc(100vh - 470px), 640px)', overflow: 'auto' }}>
            <div style={{ minWidth: 640 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: FEED_COLS, position: 'sticky', top: 0, zIndex: 1,
                background: 'var(--md-sys-color-surface-container-low)', borderBottom: '1px solid var(--md-sys-color-outline-variant)',
              }}>
                {['시각', '장비', '토픽', '값', '상태'].map((h, i) => (
                  <span key={h} style={{
                    padding: '11px 18px', font: '600 12px/16px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)', textAlign: i === 3 ? 'right' : 'left', whiteSpace: 'nowrap',
                  }}>{h}</span>
                ))}
              </div>
              {messages.length === 0 ? (
                <div style={{ padding: '48px 0', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {conn === 'open' ? '수신 대기 중… (장비가 발행하면 표시됩니다)' : '브로커에 연결 중입니다…'}
                </div>
              ) : (
                messages.map((m, i) => {
                  const am = ANOMALY_META[m.anomaly] ?? ANOMALY_META.normal;
                  return (
                    <div key={`${m.id}-${i}`} style={{
                      display: 'grid', gridTemplateColumns: FEED_COLS, alignItems: 'center',
                      borderBottom: i === messages.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                      background: i % 2 ? 'var(--md-sys-color-surface-container-lowest)' : 'transparent',
                    }}>
                      <span className="gs-num" style={{ padding: '9px 18px', font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', whiteSpace: 'nowrap' }}>
                        {fmtTime(m.timestamp)}
                      </span>
                      <span style={{ padding: '9px 18px', font: '500 13px/17px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {nameById.get(m.deviceId) ?? m.deviceId}
                      </span>
                      <span className="gs-num" style={{ padding: '9px 18px', font: '400 12px/16px var(--gs-font-mono, var(--gs-font-kr))', color: 'var(--md-sys-color-on-surface-variant)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        grosync/{m.deviceId}/{m.sensorType}
                      </span>
                      <span className="gs-num" style={{ padding: '9px 18px', textAlign: 'right', font: '600 13px/17px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', whiteSpace: 'nowrap' }}>
                        {fmtValue(m.value)}<span style={{ font: '400 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginLeft: 2 }}>{m.unit}</span>
                      </span>
                      <span style={{ padding: '9px 18px', font: `${m.anomaly === 'normal' ? 500 : 700} 12px/16px var(--gs-font-kr)`, color: am.c, whiteSpace: 'nowrap' }}>
                        {am.label}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Panel>

        {/* 장비 세션 */}
        <Panel style={{ flexShrink: 0 }} title="장비 세션" subtitle={`${devices.length}대 · 온라인 ${online}`} pad={false}>
          <div className="gs-scroll-hide" style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 680 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: SESSION_COLS, position: 'sticky', top: 0, zIndex: 1,
                background: 'var(--md-sys-color-surface-container-low)', borderBottom: '1px solid var(--md-sys-color-outline-variant)',
              }}>
                {['장비', '상태', '최근 수신', '센서', 'MQTT 계정'].map((h) => (
                  <span key={h} style={{ padding: '11px 18px', font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', whiteSpace: 'nowrap' }}>{h}</span>
                ))}
              </div>
              {devices.length === 0 ? (
                <div style={{ padding: '40px 0', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  등록된 장비가 없습니다.
                </div>
              ) : (
                devices.map((d, i) => (
                  <div key={d.id} style={{
                    display: 'grid', gridTemplateColumns: SESSION_COLS, alignItems: 'center',
                    borderBottom: i === devices.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                  }}>
                    <span style={{ padding: '11px 18px', minWidth: 0 }}>
                      <span style={{ display: 'block', font: '600 13px/17px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                      <span className="gs-num" style={{ display: 'block', font: '400 11px/15px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{d.id}</span>
                    </span>
                    <span style={{ padding: '11px 18px' }}><StatusPill s={toUiStatus(d.status)} /></span>
                    <span className="gs-num" style={{ padding: '11px 18px', font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', whiteSpace: 'nowrap' }}>
                      {fmtLastSeen(d.lastSeenAt)}
                    </span>
                    <span style={{ padding: '11px 18px', font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.sensors.length ? d.sensors.join(', ') : '—'}
                    </span>
                    <span style={{ padding: '11px 18px' }}>
                      {d.mqttApiKey ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: '600 12px/1 var(--gs-font-kr)', color: 'var(--gs-primary-50)' }}>
                          <Sym name="key" size={14} fill={1} />발급됨
                        </span>
                      ) : (
                        <span style={{ font: '500 12px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>없음</span>
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </Panel>
      </ViewScroll>
    </>
  );
}

function FeedButton({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 32, padding: '0 12px', borderRadius: 9999, cursor: 'pointer',
        border: '1px solid var(--md-sys-color-outline-variant)', background: 'transparent',
        color: 'var(--md-sys-color-on-surface-variant)', font: '600 12px/1 var(--gs-font-kr)',
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}
    >
      <Sym name={icon} size={15} />{label}
    </button>
  );
}
